import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { MapPin, Hotel, Car, Calendar, FileText, Users, Loader2, Download, Share2, Mail, Plus, Trash2, Building2, Calculator, Eye, Check, X, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { QuotationViewDoc } from '../components/QuotationView/QuotationViewDoc';

// Downloads use responseType 'blob', so the server's error message arrives as a Blob.
const blobErrorDetail = async (err, fallback) => {
  try {
    const data = err.response?.data;
    if (data instanceof Blob) return JSON.parse(await data.text()).detail || fallback;
    return data?.detail || fallback;
  } catch {
    return fallback;
  }
};
import { formatCurrency } from '../utils/helpers';
import { getAccessibleTextColor, getContrastTextColor } from '../utils/colorContrast';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

const getMealPlanLabel = (code) => {
  const labels = {
    'CP': 'Breakfast Only (CP)',
    'MAP': 'Breakfast + Dinner (MAP)',
    'AP': 'All Meals (AP)',
    'EP': 'Room Only (EP)'
  };
  return labels[code] || code;
};

const PublicQuotation = () => {
  const { shareToken } = useParams();
  const [quotation, setQuotation] = useState(null);
  const [basePrice, setBasePrice] = useState(0); // cheapest guest Grand Total before the viewer's markup
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadTheme, setDownloadTheme] = useState('regular');
  const [downloading, setDownloading] = useState(false);
  
  // Template options
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [downloadFormat, setDownloadFormat] = useState('theme'); // 'theme' or 'template'
  
  // Viewer markup options
  const [viewerMarkupType, setViewerMarkupType] = useState('percent');
  const [viewerMarkupPct, setViewerMarkupPct] = useState(0);
  const [viewerMarkupAmount, setViewerMarkupAmount] = useState(0);
  const [viewerMiscCosts, setViewerMiscCosts] = useState([]);
  
  // GST options
  const [viewerGstPct, setViewerGstPct] = useState(0);
  const [viewerGstApplyOn, setViewerGstApplyOn] = useState('total'); // 'total' or 'markup'
  
  // Markup dialog
  const [showMarkupDialog, setShowMarkupDialog] = useState(false);
  
  // Email dialog
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailHtml, setEmailHtml] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailText, setEmailText] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(false);
  
  // Template view
  const [viewMode, setViewMode] = useState('default'); // 'default' or 'template'
  const [templateHtml, setTemplateHtml] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  useEffect(() => {
    if (!quotation) return undefined;
    const markupPct = viewerMarkupType === 'percent' ? viewerMarkupPct : 0;
    const markupAmt = viewerMarkupType === 'amount' ? viewerMarkupAmount : 0;
    const timer = setTimeout(() => {
      axios.get(`${API_BASE_URL}/api/v1/public/quotation/${shareToken}`, {
        params: { viewer_markup_pct: markupPct, viewer_markup_amount: markupAmt, viewer_gst_pct: viewerGstPct, viewer_gst_apply_on: viewerGstApplyOn },
      }).then((r) => {
        setQuotation(r.data);
        if (viewMode === 'template' && selectedTemplateId) fetchTemplateView(selectedTemplateId);
      }).catch((err) => toast.error(err.response?.data?.detail || 'Could not apply markup', { id: 'markup-error' }));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerMarkupType, viewerMarkupPct, viewerMarkupAmount, viewerGstPct, viewerGstApplyOn]);

  useEffect(() => {
    const h = quotation?.view?.sections?.header;
    if (h) document.title = `${h.quote_reference} – ${h.package_title} – ${h.agency_name}`;
  }, [quotation]);

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/v1/public/quotation/${shareToken}`);
        setQuotation(response.data);
        setBasePrice(response.data.net_quoted_price || 0);
      } catch (err) {
        setError(err.response?.data?.detail || 'Quotation not found or link expired');
      } finally {
        setLoading(false);
      }
    };

    if (shareToken) {
      fetchQuotation();
    }
  }, [shareToken]);
  
  // Fetch available templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/public/quotation/${shareToken}/templates`);
        setTemplates(response.data.templates || []);
        if (response.data.default_template_id) {
          setSelectedTemplateId(response.data.default_template_id);
          // Auto-fetch template view when default template exists
          fetchTemplateView(response.data.default_template_id);
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      }
    };

    if (shareToken) {
      fetchTemplates();
    }
  }, [shareToken]);

  // Calculate final price with viewer markup
  const calculateFinalPrice = () => {
    let additionalMarkup = 0;
    
    if (viewerMarkupType === 'percent' && viewerMarkupPct > 0) {
      additionalMarkup = basePrice * (viewerMarkupPct / 100);
    } else if (viewerMarkupType === 'amount') {
      additionalMarkup = viewerMarkupAmount;
    }
    
    const miscTotal = viewerMiscCosts.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
    
    // Calculate GST
    let gstAmount = 0;
    if (viewerGstPct > 0) {
      if (viewerGstApplyOn === 'total') {
        // GST on total (base + markup + misc)
        gstAmount = (basePrice + additionalMarkup + miscTotal) * (viewerGstPct / 100);
      } else {
        // GST on markup only
        gstAmount = additionalMarkup * (viewerGstPct / 100);
      }
    }
    
    return basePrice + additionalMarkup + miscTotal + gstAmount;
  };
  
  // Calculate GST amount for display
  const calculateGstAmount = () => {
    let additionalMarkup = 0;
    
    if (viewerMarkupType === 'percent' && viewerMarkupPct > 0) {
      additionalMarkup = basePrice * (viewerMarkupPct / 100);
    } else if (viewerMarkupType === 'amount') {
      additionalMarkup = viewerMarkupAmount;
    }
    
    const miscTotal = viewerMiscCosts.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
    
    if (viewerGstPct <= 0) return 0;
    
    if (viewerGstApplyOn === 'total') {
      return (basePrice + additionalMarkup + miscTotal) * (viewerGstPct / 100);
    } else {
      return additionalMarkup * (viewerGstPct / 100);
    }
  };

  // Add miscellaneous cost
  const addMiscCost = () => {
    setViewerMiscCosts([...viewerMiscCosts, { id: Date.now(), remarks: '', amount: 0 }]);
  };

  // Update misc cost
  const updateMiscCost = (id, field, value) => {
    setViewerMiscCosts(viewerMiscCosts.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  // Remove misc cost
  const removeMiscCost = (id) => {
    setViewerMiscCosts(viewerMiscCosts.filter(c => c.id !== id));
  };

  // Download PDF
  const downloadPDF = async () => {
    try {
      setDownloading(true);
      const markupPct = viewerMarkupType === 'percent' ? viewerMarkupPct : 0;
      const markupAmt = viewerMarkupType === 'amount' ? viewerMarkupAmount : 0;
      
      // Build URL with template or theme + GST params
      let url = `${API_BASE_URL}/api/v1/public/quotation/${shareToken}/pdf?viewer_markup_pct=${markupPct}&viewer_markup_amount=${markupAmt}&viewer_gst_pct=${viewerGstPct}&viewer_gst_apply_on=${viewerGstApplyOn}`;
      if (downloadFormat === 'template' && selectedTemplateId) {
        url += `&template_id=${selectedTemplateId}`;
      } else {
        url += `&theme=${downloadTheme}`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${quotation.quote_reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('PDF downloaded successfully');
      setShowDownloadModal(false);
    } catch (err) {
      toast.error(await blobErrorDetail(err, 'Failed to download PDF'));
    } finally {
      setDownloading(false);
    }
  };

  // Download Word
  const downloadWord = async () => {
    try {
      setDownloading(true);
      const markupPct = viewerMarkupType === 'percent' ? viewerMarkupPct : 0;
      const markupAmt = viewerMarkupType === 'amount' ? viewerMarkupAmount : 0;
      
      // Build URL with template or theme + GST params
      let url = `${API_BASE_URL}/api/v1/public/quotation/${shareToken}/docx?viewer_markup_pct=${markupPct}&viewer_markup_amount=${markupAmt}&viewer_gst_pct=${viewerGstPct}&viewer_gst_apply_on=${viewerGstApplyOn}`;
      if (downloadFormat === 'template' && selectedTemplateId) {
        url += `&template_id=${selectedTemplateId}`;
      } else {
        url += `&theme=${downloadTheme}`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${quotation.quote_reference}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Word document downloaded successfully');
      setShowDownloadModal(false);
    } catch (err) {
      toast.error(await blobErrorDetail(err, 'Failed to download Word document'));
    } finally {
      setDownloading(false);
    }
  };

  // WhatsApp message built on the server from the guest Quotation View (with the viewer's markup).
  const fetchWhatsAppText = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/v1/public/quotation/${shareToken}/whatsapp-text`, {
      params: {
        link: window.location.href,
        viewer_markup_pct: viewerMarkupType === 'percent' ? viewerMarkupPct : 0,
        viewer_markup_amount: viewerMarkupType === 'amount' ? viewerMarkupAmount : 0,
        viewer_gst_pct: viewerGstPct,
        viewer_gst_apply_on: viewerGstApplyOn,
      },
    });
    return response.data.text;
  };

  const copyWhatsApp = async () => {
    try {
      await navigator.clipboard.writeText(await fetchWhatsAppText());
      toast.success('WhatsApp message copied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to copy WhatsApp message');
    }
  };

  const shareWhatsApp = async () => {
    const win = window.open('', '_blank');
    try {
      const url = `https://wa.me/?text=${encodeURIComponent(await fetchWhatsAppText())}`;
      if (win) win.location.href = url; else window.open(url, '_blank');
    } catch (err) {
      if (win) win.close();
      toast.error(err.response?.data?.detail || 'Failed to prepare WhatsApp message');
    }
  };

  // Share via Email
  const shareEmail = async () => {
    // Open the email dialog and fetch HTML
    setShowEmailDialog(true);
    setLoadingEmail(true);
    
    try {
      const markupPct = viewerMarkupType === 'percent' ? viewerMarkupPct : 0;
      const markupAmt = viewerMarkupType === 'amount' ? viewerMarkupAmount : 0;
      
      const response = await axios.get(`${API_BASE_URL}/api/v1/public/quotation/${shareToken}/email-html`, {
        params: {
          viewer_markup_pct: markupPct,
          viewer_markup_amount: markupAmt,
          viewer_gst_pct: viewerGstPct,
          viewer_gst_apply_on: viewerGstApplyOn,
          template_id: selectedTemplateId || undefined,
          link: window.location.href,
          origin: window.location.origin,
        },
      });
      setEmailHtml(response.data.html);
      setEmailSubject(response.data.subject);
      setEmailText(response.data.text || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate email');
      setShowEmailDialog(false);
    } finally {
      setLoadingEmail(false);
    }
  };
  
  const copyEmailHtml = async () => {
    try {
      await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([emailHtml], { type: 'text/html' }),
        'text/plain': new Blob([emailText || emailHtml], { type: 'text/plain' }),
      })]);
      toast.success('Email copied — paste it into your email');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = emailHtml;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('HTML copied to clipboard!');
    }
  };
  
  const openMailto = () => {
    const subject = encodeURIComponent(emailSubject);
    const body = encodeURIComponent(`Dear Guest,\n\nPlease find the tour package quotation:\n\n${emailText}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };
  
  // Fetch template-rendered view
  const fetchTemplateView = async (templateId) => {
    if (!templateId) {
      setViewMode('default');
      return;
    }
    
    setLoadingTemplate(true);
    try {
      const markupPct = viewerMarkupType === 'percent' ? viewerMarkupPct : 0;
      const markupAmt = viewerMarkupType === 'amount' ? viewerMarkupAmount : 0;
      
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/public/quotation/${shareToken}/rendered?template_id=${templateId}&viewer_markup_pct=${markupPct}&viewer_markup_amount=${markupAmt}&viewer_gst_pct=${viewerGstPct}&viewer_gst_apply_on=${viewerGstApplyOn}`
      );
      
      if (response.data.success) {
        setTemplateHtml(response.data.html);
        setViewMode('template');
      } else {
        toast.error(response.data.error || 'Failed to load template');
        setViewMode('default');
      }
    } catch (err) {
      toast.error('Failed to load template view');
      setViewMode('default');
    } finally {
      setLoadingTemplate(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-navy" />
          <p className="mt-2 text-gray-600">Loading quotation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Quotation Not Found</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Everything shown comes from the server-built guest Quotation View (one content model).
  const view = quotation?.view;
  const sharedHeader = view ? { packageTitle: view.sections.header.package_title, routeSummary: view.sections.route.text } : null;
  const viewGrand = view?.sections?.pricing?.tables?.[0]?.rows?.[0]?.grand_total?.text;
  const agency = quotation?.agency;
  
  // Get brand colors from agency, falling back to the OnTrOf defaults.
  // `primaryColor` drives the gradient banner (raw); `--navy`/`--cyan` below
  // drive every other themed element and are contrast-corrected so a very
  // light DMC color never becomes unreadable text. UI Fix Brief (2026-02).
  const primaryColor = agency?.primary_color || '#0A1F44';
  const secondaryColor = agency?.secondary_color || '#00C8E8';
  const themeNavy = getAccessibleTextColor(primaryColor);
  const accentTextColor = getContrastTextColor(secondaryColor);
  // h1-h6 get a fixed `color: var(--navy)` from the global heading style
  // (@layer base), which otherwise wins over the banner's inherited
  // text-white. Give them an explicit, contrast-safe color instead.
  const bannerTextColor = getContrastTextColor(primaryColor);
  const publicThemeStyle = { '--navy': themeNavy, '--cyan': secondaryColor, '--cyan-strong': getAccessibleTextColor(secondaryColor) };

  return (
    <div
      className="min-h-screen bg-gray-100 py-4 sm:py-8 px-2 sm:px-4 quotation-theme"
      style={publicThemeStyle}
    >
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
        
        {/* Header with Logo and Gradient - using tenant colors */}
        <div 
          className="text-white p-4 sm:p-6 rounded-lg shadow-lg"
          style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
        >
          {/* Agency Info with Logo */}
          {agency && (
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/20">
              {agency.logo_url && (
                <img 
                  src={agency.logo_url} 
                  alt={agency.agency_name}
                  className="h-10 max-w-[100px] object-contain bg-white/90 p-1 rounded"
                />
              )}
              <div>
                <h2 className="text-lg font-bold" style={{ color: bannerTextColor }}>{agency.agency_name}</h2>
                <p className="text-sm opacity-80" style={{ color: bannerTextColor }}>
                  {agency.email && <span>{agency.email}</span>}
                  {agency.email && agency.phone && <span> | </span>}
                  {agency.phone && <span>{agency.phone}</span>}
                </p>
              </div>
            </div>
          )}
          
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: bannerTextColor }}>{sharedHeader?.packageTitle || `${quotation.total_days}D / ${quotation.total_nights}N Tour Package`}</h1>
          <p className="opacity-90 mt-1 text-sm sm:text-base" style={{ color: bannerTextColor }}>
            {sharedHeader?.routeSummary}
          </p>
          <div
            className="inline-block px-3 py-1 rounded-full text-sm mt-3"
            style={{ backgroundColor: 'color-mix(in srgb, white 25%, transparent)', color: bannerTextColor }}
          >
            Quote Ref: {quotation.quote_reference}
          </div>
        </div>

        {/* Action Buttons */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:justify-center">
              <Button onClick={() => setShowDownloadModal(true)} className="h-11 bg-[var(--navy)] hover:bg-[color-mix(in_srgb,var(--navy)_90%,white)] text-white" data-testid="public-download-btn">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button variant="outline" onClick={() => setShowMarkupDialog(true)} className="h-11" title="Add your own markup / GST before sharing" data-testid="public-markup-btn">
                <Calculator className="w-4 h-4 mr-2 text-[var(--navy)]" />
                Markup
              </Button>
              <Button variant="outline" onClick={shareWhatsApp} className="h-11" title="Share via WhatsApp" data-testid="public-whatsapp-btn">
                <Share2 className="w-4 h-4 mr-2 text-[var(--navy)]" />
                WhatsApp
              </Button>
              <Button variant="outline" onClick={copyWhatsApp} className="h-11" title="Copy the WhatsApp message text" data-testid="public-copy-whatsapp-btn">
                <Copy className="w-4 h-4 mr-2 text-[var(--navy)]" />
                Copy WhatsApp
              </Button>
              <Button variant="outline" onClick={shareEmail} className="h-11" title="Share via Email" data-testid="public-email-btn">
                <Mail className="w-4 h-4 mr-2 text-[var(--navy)]" />
                Email
              </Button>
            </div>
            
            {/* View Mode Toggle */}
            {templates.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-center gap-2">
                  <Label className="text-sm text-gray-600">View as:</Label>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={viewMode === 'default' ? 'default' : 'outline'}
                      onClick={() => setViewMode('default')}
                      data-testid="public-view-default-btn"
                      className="h-8 text-xs"
                    >
                      Default
                    </Button>
                    <Select
                      value={viewMode === 'template' ? selectedTemplateId : ''}
                      onValueChange={(val) => {
                        if (val) {
                          setSelectedTemplateId(val);
                          fetchTemplateView(val);
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid="public-template-select">
                        <Eye className="w-3 h-3 mr-1" />
                        <SelectValue placeholder="Template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map(t => (
                          <SelectItem key={t.template_id} value={t.template_id}>
                            {t.template_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show applied markup/GST indicator */}
            {(viewerMarkupPct > 0 || viewerMarkupAmount > 0 || viewerGstPct > 0) && (
              <div className="mt-3 p-2 bg-[color-mix(in_srgb,var(--cyan)_10%,white)] rounded-lg border border-[color-mix(in_srgb,var(--navy)_15%,transparent)] text-center">
                <span className="text-sm text-[var(--navy)]">
                  {(viewerMarkupPct > 0 || viewerMarkupAmount > 0) && (
                    <>Markup: {viewerMarkupType === 'percent' ? `${viewerMarkupPct}%` : `₹${viewerMarkupAmount.toLocaleString('en-IN')}`}</>
                  )}
                  {(viewerMarkupPct > 0 || viewerMarkupAmount > 0) && viewerGstPct > 0 && ' + '}
                  {viewerGstPct > 0 && (
                    <>GST: {viewerGstPct}% on {viewerGstApplyOn === 'total' ? 'Total' : 'Markup'}</>
                  )}
                  <span className="ml-2 text-[var(--navy)] font-semibold">
                    (Final: {viewGrand || formatCurrency(calculateFinalPrice())})
                  </span>
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Template View or Default View */}
        {loadingTemplate ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-navy" />
              <p className="mt-2 text-gray-600">Loading template view...</p>
            </CardContent>
          </Card>
        ) : viewMode === 'template' && templateHtml ? (
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <iframe
                srcDoc={templateHtml}
                className="w-full min-h-[800px] border-0"
                title="Quotation Template View"
                sandbox="allow-same-origin"
              />
            </CardContent>
          </Card>
        ) : (
          <QuotationViewDoc view={view} />
        )}
      </div>

      {/* Download Modal with Markup Options */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent className="max-w-md quotation-theme" style={publicThemeStyle}>
          <DialogHeader>
            <DialogTitle>Download Quotation</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Format Type Selection */}
            <div className="space-y-2">
              <Label>Download Format</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={downloadFormat === 'theme' ? 'default' : 'outline'}
                  onClick={() => setDownloadFormat('theme')}
                  className="flex-1"
                >
                  Theme Style
                </Button>
                {templates.length > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    variant={downloadFormat === 'template' ? 'default' : 'outline'}
                    onClick={() => setDownloadFormat('template')}
                    className="flex-1"
                  >
                    Template
                  </Button>
                )}
              </div>
            </div>
            
            {/* Theme Selection (when theme format selected) */}
            {downloadFormat === 'theme' && (
              <div className="space-y-2">
                <Label>Theme Style</Label>
                <Select value={downloadTheme} onValueChange={setDownloadTheme}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Short - Essential info only</SelectItem>
                    <SelectItem value="regular">Regular - Balanced details</SelectItem>
                    <SelectItem value="detailed">Detailed - Full information</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Template Selection (when template format selected) */}
            {downloadFormat === 'template' && templates.length > 0 && (
              <div className="space-y-2">
                <Label>Select Template</Label>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.template_name} {t.is_default && '(Default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Viewer Markup Section */}
            <div className="space-y-3 p-3 bg-[color-mix(in_srgb,var(--cyan)_10%,white)] rounded-lg border border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
              <Label className="text-sm font-semibold">Add Your Markup (Optional)</Label>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={viewerMarkupType === 'percent' ? 'default' : 'outline'}
                  onClick={() => setViewerMarkupType('percent')}
                  className="flex-1 h-8 text-xs"
                >
                  Percentage (%)
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewerMarkupType === 'amount' ? 'default' : 'outline'}
                  onClick={() => setViewerMarkupType('amount')}
                  className="flex-1 h-8 text-xs"
                >
                  Fixed Amount (₹)
                </Button>
              </div>
              
              <Input
                type="number"
                min="0"
                placeholder={viewerMarkupType === 'percent' ? 'Enter % markup' : 'Enter amount'}
                value={viewerMarkupType === 'percent' ? viewerMarkupPct : viewerMarkupAmount}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  if (viewerMarkupType === 'percent') {
                    setViewerMarkupPct(val);
                  } else {
                    setViewerMarkupAmount(val);
                  }
                }}
                className="h-9"
              />
            </div>

            {/* Miscellaneous Costs */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Additional Costs</Label>
                <Button type="button" size="sm" variant="outline" onClick={addMiscCost} className="h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              
              {viewerMiscCosts.map(cost => (
                <div key={cost.id} className="flex gap-2">
                  <Input
                    placeholder="Description"
                    value={cost.remarks}
                    onChange={(e) => updateMiscCost(cost.id, 'remarks', e.target.value)}
                    className="flex-1 h-8 text-sm"
                  />
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={cost.amount}
                    onChange={(e) => updateMiscCost(cost.id, 'amount', e.target.value)}
                    className="w-24 h-8 text-sm"
                  />
                  <Button type="button" size="sm" variant="ghost" onClick={() => removeMiscCost(cost.id)} className="h-8 w-8 p-0 text-error">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Price Summary */}
            <div className="p-3 bg-[color-mix(in_srgb,var(--navy)_5%,white)] rounded-lg border border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Original Price:</span>
                <span className="font-semibold">{formatCurrency(basePrice)}</span>
              </div>
              {(viewerMarkupPct > 0 || viewerMarkupAmount > 0 || viewerMiscCosts.length > 0) && (
                <>
                  <div className="flex justify-between items-center text-sm text-gray-600 mt-1">
                    <span>+ Your Markup:</span>
                    <span>{formatCurrency(calculateFinalPrice() - basePrice)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
                    <span className="font-semibold">Final Price:</span>
                    <span className="font-bold text-[var(--navy)]">{formatCurrency(calculateFinalPrice())}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDownloadModal(false)}>Cancel</Button>
            <Button onClick={downloadPDF} disabled={downloading} className="bg-[var(--navy)] hover:bg-[color-mix(in_srgb,var(--navy)_90%,white)] text-white" data-testid="public-download-pdf-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              PDF
            </Button>
            <Button onClick={downloadWord} disabled={downloading} className="border-transparent hover:opacity-90" style={{ backgroundColor: 'var(--cyan)', color: accentTextColor }} data-testid="public-download-word-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Word
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Markup Dialog */}
      <Dialog open={showMarkupDialog} onOpenChange={setShowMarkupDialog}>
        <DialogContent className="max-w-md quotation-theme" style={publicThemeStyle}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[var(--navy)]" />
              Apply Markup & GST
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Markup Type Selection */}
            <div className="space-y-2">
              <Label>Markup Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={viewerMarkupType === 'percent' ? 'default' : 'outline'}
                  onClick={() => setViewerMarkupType('percent')}
                  data-testid="markup-type-percent-btn"
                  className="flex-1"
                >
                  Percentage (%)
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewerMarkupType === 'amount' ? 'default' : 'outline'}
                  onClick={() => setViewerMarkupType('amount')}
                  data-testid="markup-type-amount-btn"
                  className="flex-1"
                >
                  Fixed Amount (₹)
                </Button>
              </div>
            </div>
            
            {/* Markup Value Input */}
            <div className="space-y-2">
              <Label>{viewerMarkupType === 'percent' ? 'Markup %' : 'Markup Amount (₹)'}</Label>
              <Input
                type="number"
                min="0"
                step={viewerMarkupType === 'percent' ? '0.5' : '100'}
                placeholder={viewerMarkupType === 'percent' ? 'Enter percentage' : 'Enter amount'}
                value={viewerMarkupType === 'percent' ? (viewerMarkupPct || '') : (viewerMarkupAmount || '')}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  if (viewerMarkupType === 'percent') {
                    setViewerMarkupPct(val);
                    setViewerMarkupAmount(0);
                  } else {
                    setViewerMarkupAmount(val);
                    setViewerMarkupPct(0);
                  }
                }}
                className="text-lg"
                data-testid="markup-value-input"
              />
            </div>
            
            {/* GST Section */}
            <div className="space-y-3 p-3 bg-[color-mix(in_srgb,var(--navy)_5%,white)] rounded-lg border border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
              <Label className="text-sm font-semibold text-[var(--navy)]">GST Configuration</Label>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">GST %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="28"
                    step="0.5"
                    placeholder="0"
                    value={viewerGstPct || ''}
                    onChange={(e) => setViewerGstPct(parseFloat(e.target.value) || 0)}
                    data-testid="markup-gst-pct-input"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Apply GST On</Label>
                  <Select value={viewerGstApplyOn} onValueChange={setViewerGstApplyOn}>
                    <SelectTrigger className="h-9" data-testid="markup-gst-apply-on-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="total">Total Amount</SelectItem>
                      <SelectItem value="markup">Markup Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="p-4 bg-[color-mix(in_srgb,var(--cyan)_10%,white)] rounded-lg border border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Base Total (Grand Total):</span>
                <span className="font-semibold text-gray-800">{formatCurrency(basePrice)}</span>
              </div>
              
              {(viewerMarkupPct > 0 || viewerMarkupAmount > 0) && (
                <div className="flex justify-between items-center text-sm text-[var(--navy)] mb-2">
                  <span>+ Markup ({viewerMarkupType === 'percent' ? `${viewerMarkupPct}%` : 'Fixed'}):</span>
                  <span>+{formatCurrency(
                    viewerMarkupType === 'percent' 
                      ? basePrice * viewerMarkupPct / 100 
                      : viewerMarkupAmount
                  )}</span>
                </div>
              )}
              
              {viewerGstPct > 0 && (
                <div className="flex justify-between items-center text-sm text-[var(--navy)] mb-2">
                  <span>+ GST ({viewerGstPct}% on {viewerGstApplyOn === 'total' ? 'Total' : 'Markup'}):</span>
                  <span>+{formatCurrency(calculateGstAmount())}</span>
                </div>
              )}
              
              {(viewerMarkupPct > 0 || viewerMarkupAmount > 0 || viewerGstPct > 0) && (
                <div className="flex justify-between items-center pt-2 border-t border-[color-mix(in_srgb,var(--navy)_20%,transparent)]">
                  <span className="font-bold text-gray-800">Final Price:</span>
                  <span className="font-bold text-xl text-[var(--navy)]" data-testid="markup-final-price">{formatCurrency(calculateFinalPrice())}</span>
                </div>
              )}
              
              {viewerMarkupPct === 0 && viewerMarkupAmount === 0 && viewerGstPct === 0 && (
                <div className="text-center text-sm text-gray-500 mt-2">
                  Enter markup or GST values above to see adjusted price
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setViewerMarkupPct(0);
                setViewerMarkupAmount(0);
                setViewerGstPct(0);
                setShowMarkupDialog(false);
              }}
              data-testid="markup-clear-close-btn"
            >
              Clear & Close
            </Button>
            <Button 
              onClick={() => setShowMarkupDialog(false)} 
              data-testid="markup-apply-btn"
              className="border-transparent hover:opacity-90"
              style={{ backgroundColor: 'var(--cyan)', color: accentTextColor }}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] quotation-theme" style={publicThemeStyle}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[var(--navy)]" />
              Email Preview
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingEmail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--navy)]" />
                <span className="ml-2">Generating email...</span>
              </div>
            ) : (
              <>
                {/* Subject */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Subject</Label>
                  <div className="p-2 bg-gray-100 rounded text-sm">{emailSubject}</div>
                </div>
                
                {/* Template Selector */}
                {templates.length > 0 && (
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">Email Template</Label>
                    <Select 
                      value={selectedTemplateId} 
                      onValueChange={(val) => {
                        setSelectedTemplateId(val);
                        // Re-fetch email with new template
                        shareEmail();
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Default email format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Default Format</SelectItem>
                        {templates.map(t => (
                          <SelectItem key={t.template_id} value={t.template_id}>
                            {t.template_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* HTML Preview */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Preview</Label>
                  <div 
                    className="border rounded-lg p-4 bg-white max-h-[300px] overflow-auto email-html-preview"
                    dangerouslySetInnerHTML={{ __html: emailHtml }}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Close</Button>
            <Button variant="outline" onClick={openMailto} disabled={loadingEmail}>
              <Mail className="w-4 h-4 mr-2 text-[var(--navy)]" />
              Open in Email App
            </Button>
            <Button onClick={copyEmailHtml} disabled={loadingEmail} className="bg-[var(--navy)] hover:bg-[color-mix(in_srgb,var(--navy)_90%,white)] text-white">
              <FileText className="w-4 h-4 mr-2" />
              Copy HTML
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicQuotation;
